import { t } from '@web/i18n';
import { AppIcons } from '@web/ui/core';
import { AppBar, SegmentedButton } from '@web/ui/core/organisms';

import type { BoardMode } from '@web/ui/board/hooks/use_board';

export interface BoardTopBarProps {
  mode: BoardMode;
  onChangeMode: (mode: BoardMode) => void;
  projectName: string;
  squadName: string;
}

/**
 * Squad board top bar — crumbtrail on the left, Conversas/Tarefas
 * segmented control in the centre, no right-side slot. Composes the
 * shared `AppBar` shell with the canonical `SegmentedButton` for the
 * mode switch.
 */
export function BoardTopBar({
  mode,
  onChangeMode,
  projectName,
  squadName,
}: Readonly<BoardTopBarProps>): React.JSX.Element {
  return (
    <AppBar
      leading={
        <nav
          aria-label={t('board.topBar.crumbAria')}
          className="inline-flex min-w-0 items-center gap-2 font-mono text-tiny uppercase tracking-eyebrow text-muted-foreground whitespace-nowrap"
        >
          <AppIcons name="house" size={14} className="text-faint-foreground" />
          <span className="truncate">{projectName}</span>
          <span className="text-faint-foreground">/</span>
          <span className="truncate font-medium text-foreground">{squadName}</span>
        </nav>
      }
      center={
        <SegmentedButton ariaLabel={t('workspace.topBar.mode.aria')}>
          <SegmentedButton.Item
            icon={<AppIcons name="chat-circle" size={14} />}
            label={t('workspace.topBar.mode.conversations')}
            isActive={mode === 'conversations'}
            onClick={() => {
              onChangeMode('conversations');
            }}
          />
          <SegmentedButton.Item
            icon={<AppIcons name="list-checks" size={14} />}
            label={t('workspace.topBar.mode.tasks')}
            isActive={mode === 'tasks'}
            onClick={() => {
              onChangeMode('tasks');
            }}
          />
        </SegmentedButton>
      }
    />
  );
}
